import React, { useState } from "react";
import axios from "axios";
import { Leaf, Check } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Gentle, minimal subscription section used at the bottom of filtered
 * Journal views. Not sticky, not pinned — a quiet ending.
 */
export default function JournalSubscribe() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle");
  const [message, setMessage] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setStatus("error");
      setMessage("Please share a valid email address.");
      return;
    }
    setStatus("loading");
    try {
      await axios.post(`${API}/subscribe`, { email });
      setStatus("success");
      setMessage("You're in. We're glad you're here.");
      setEmail("");
    } catch (err) {
      setStatus("error");
      setMessage(
        err?.response?.data?.detail
          ? String(err.response.data.detail)
          : "Something went quiet. Please try again."
      );
    }
  };

  return (
    <section
      data-testid="journal-subscribe"
      className="bg-background"
    >
      <div className="max-w-2xl mx-auto px-6 sm:px-8 py-28 sm:py-40 text-center">
        <Leaf
          size={20}
          strokeWidth={1.25}
          className="mx-auto text-primary/80 leaf-sway"
          aria-hidden="true"
        />
        <h2 className="mt-8 font-serif text-3xl sm:text-4xl lg:text-5xl font-light leading-tight">
          Stay connected
        </h2>
        <p className="mt-5 text-sm sm:text-base text-muted-foreground leading-relaxed max-w-md mx-auto">
          Gentle reflections delivered occasionally for grandparents, parents,
          and little ones.
        </p>

        <form
          onSubmit={handleSubmit}
          data-testid="journal-subscribe-form"
          className="mt-12 max-w-md mx-auto flex items-end gap-4"
        >
          <div className="flex-1 text-left">
            <label
              htmlFor="journal-subscribe-email"
              className="sr-only"
            >
              Email address
            </label>
            <input
              id="journal-subscribe-email"
              data-testid="journal-subscribe-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              className="quiet-input text-center sm:text-left"
              disabled={status === "loading"}
            />
          </div>
          <button
            data-testid="journal-subscribe-submit"
            type="submit"
            disabled={status === "loading"}
            className="px-6 sm:px-7 py-3 rounded-full bg-primary/90 text-primary-foreground text-sm tracking-wide hover:bg-primary transition-colors duration-500 disabled:opacity-60 flex-shrink-0 inline-flex items-center gap-2"
          >
            {status === "success" ? (
              <>
                Joined <Check size={15} strokeWidth={1.5} />
              </>
            ) : (
              "Subscribe"
            )}
          </button>
        </form>

        {message && (
          <p
            data-testid="journal-subscribe-message"
            className={`mt-6 text-sm italic font-serif ${
              status === "error" ? "text-destructive" : "text-primary"
            }`}
          >
            {message}
          </p>
        )}
      </div>
    </section>
  );
}
